export interface CaseStudy {
  id: string; // Unique identifier
  title: string;
  clientType: string; // Sector
  duration: string;
  imageUrl: string; // Placeholder image path
  situation: string;
  approach: string;
  results: string[];
  relatedService: 'process-mapping' | 'custom-reporting' | 'operations-launch' | 'tech-creation';
}

// Updated Case Studies based on LinkedIn profile examples
export const caseStudies: CaseStudy[] = [
    { // NEW Case Study based on TalkTalk Role
        id: 'delivery-method-rationalisation-telecom',
        title: 'Rationalising Technology Delivery Methods For Major UK Telecoms Provider',
        clientType: 'Telecommunications',
        duration: '3 mos',
        imageUrl: '/case-studies/rationalise.webp', // Placeholder - ensure this image exists or use another
        situation: 'A major UK telecoms provider was using over 20 different technology delivery methodologies (including agile, waterfall, and iterative variations) across the organisation, leading to inconsistency and complexity in project governance and execution.',
        approach: 'Analysed the existing delivery methods and project types. Consolidated the diverse approaches into three core, standardised methodologies tailored to different needs: an agile/sprint-based process for front-end/digital changes, and a more structured waterfall approach for sequential changes impacting complex legacy systems (e.g., billing) requiring thorough impact assessment.',
        results: [
            'Simplified technology delivery governance by reducing methodologies from over 20 to 3.',
            'Established a consistent framework for project execution across the organisation.',
            'Enabled teams to select the most appropriate delivery method based on project type (agile vs. waterfall).',
            'Improved clarity and efficiency in managing technology changes.'
        ],
        relatedService: 'process-mapping',
    },
    {
        id: 'ivr-optimisation-energy', // Keeping this specific ID for internal reference if needed
        title: 'IVR Optimisation & Self-Service Enhancement At Major UK Energy Retailer', // Anonymised Title
        clientType: 'Energy Retail (Customer Services)',
        duration: '6 mos', // Corrected duration based on previous update
        imageUrl: '/case-studies/ivr.webp', // UPDATED
        situation: 'A major UK energy retailer aimed to improve customer experience and reduce operational costs by increasing the effectiveness of its Interactive Voice Response (IVR) system for self-service and identity verification.',
        approach: 'Programme managed a dedicated team across nine distinct initiatives focused on redesigning IVR call flows, implementing enhanced self-service functionalities, and improving customer verification processes within the phone system.',
        results: [
            'Achieved record-high IVR self-service and verification volumes.',
            'Reduced operational costs by £4 million per annum compared to the baseline.', // Added specific saving
            'Successfully managed and delivered multiple concurrent IVR improvement projects.',
            'Enhanced customer experience through more efficient self-service options.'
        ],
        relatedService: 'process-mapping', // Corrected based on previous update
    },
    { // NEW Case Study: Phantom Inventory Tool
        id: 'phantom-inventory-tool-retail',
        title: 'Developing Phantom Inventory Identification Tool For National Food Retailer',
        clientType: 'Food Retail',
        duration: '5 mos', // Example duration
        imageUrl: '/case-studies/inventory.webp', // Placeholder - ensure this image exists
        situation: 'A national food retailer faced challenges with "phantom inventory" – stock shown in the system but physically missing from shelves. This impacted availability and sales, particularly for chilled and ambient goods, and led to inefficient stock counting.',
        approach: 'Developed two targeted tools: 1) A tool for chilled items that flagged potential phantoms based on last known delivery date and product shelf life. 2) A secondary tool for ambient items that identified likely phantoms by comparing sales rates with similar-sized stores.',
        results: [
            'Enabled stores to proactively target checks on high-risk phantom inventory items.',
            'Contributed to recovering over £4 million in annual revenue by improving availability of previously "phantom" stock.',
            'Informed the development of a more targeted and efficient stock counting strategy.',
            'Maximised on-shelf availability and potential sales across chilled and ambient ranges.'
        ],
        relatedService: 'tech-creation',
    },
    { // UPDATED Case Study based on National Grid BA Role
        id: 'capacity-market-tool-gov',
        title: 'Developing Bespoke IT Solution For UK Capacity Market Administration',
        clientType: 'Government Programme',
        duration: '7 mos',
        imageUrl: '/case-studies/EMR.webp', // UPDATED
        situation: 'A major UK energy distributior, acting on behalf of a government reform programme, required a new, bespoke IT solution to administer complex Capacity Market auctions and manage subsequent agreements.',
        approach: 'Acted as Business Analyst, overseeing the requirements capture process with stakeholders. Liaised with development teams during the build phase to ensure the solution met the specific needs for auction administration and agreement management.',
        results: [
            'Successfully defined and documented requirements for a critical bespoke IT system.',
            'Facilitated the development process by bridging the gap between business needs and technical teams.',
            'Contributed to the creation and testing of a system capable of managing complex energy market auctions.',
            'Ensured the solution aligned with the objectives of the UK Government\'s reform programme.'
        ],
        relatedService: 'tech-creation', // Fits launching a new system/process
    },
    { // UPDATED Case Study based on O2 Wi-Fi Role (Focus on VBA Tool)
        id: 'wifi-stagegate-tool-telecom',
        title: 'Developing Stage Gate Alignment Tool For A Public WiFi Provider',
        clientType: 'Telecommunications',
        duration: '4 mos',
        imageUrl: '/case-studies/stagegates.webp', // UPDATED
        situation: 'A major UK telecoms provider\'s Wi-Fi division needed to align its project delivery stage gates with the wider enterprise business to ensure consistency. Tracking progress against these new gates required a simple, accessible tool.',
        approach: 'Analysed the newly defined stage gate requirements. Developed a custom tool using Excel VBA to help teams track project progress, manage documentation checklists, and report status against the aligned delivery framework.',
        results: [
          'Created a user-friendly VBA tool to support the adoption of standardised stage gates.',
          'Improved visibility of project progress within the Wi-Fi division.',
          'Facilitated consistent handling of activities from venture identification to service rollout.',
          'Reduced manual effort in tracking stage gate compliance.'
        ],
        relatedService: 'tech-creation',
      },
      { // UPDATED Case Study based on Digital Retail Solutions PM Role
        id: 'retail-returns-process-rollout',
        title: 'Implementing In-Store Online Returns Process For Major UK Retailer',
        clientType: 'Clothing & Homeware Retail',
        duration: '8 mos',
        imageUrl: '/case-studies/return.webp', // UPDATED
        situation: 'A major UK retailer needed a scalable solution to handle online Clothing & Home returns within its extensive network of food-focused stores (500+ locations) lacking prior infrastructure for this.',
        approach: 'Managed the project to design, procure, and roll out the necessary technology and operational procedures, including new equipment and an app-based refund process deployable across hundreds of stores.',
        results: [
            'Successfully rolled out a new returns process in over 500 stores.',
            'Enabled significant logistics cost savings (est. £2 million) via local consolidation.',
            'Designed and implemented a scalable solution using new hardware and software (app).',
            'Improved customer convenience with more return locations.'
        ],
        relatedService: 'operations-launch',
    },
    { // UPDATED Case Study based on Mobile App Transition Role
        id: 'mobile-app-migration-energy',
        title: 'Managing Customer Migration To New Mobile App Platform for Home Energy Provider',
        clientType: 'Home Heating Services',
        duration: '4 mos',
        imageUrl: '/case-studies/energy.webp', // UPDATED
        situation: 'A major provider of home heating & repair services needed to migrate approximately 680,000 customers from a legacy mobile application to a newly developed strategic platform with minimal disruption and maximum adoption.',
        approach: 'Analysed functional/performance differences between apps. Developed a phased migration plan including a customer segmentation strategy for targeted in-app messaging and marketing campaigns to guide users through the transition.',
        results: [
            'Supported the successful migration of a large customer base (680k users).',
            'Developed a phased migration plan minimising negative customer impact.',
            'Created the customer segmentation strategy for targeted communications.',
            'Contributed to the successful launch and adoption of the new strategic mobile app.'
        ],
        relatedService: 'operations-launch',
    },
    { // UPDATED Case Study based on Lead Insight & Reporting Manager Role
        id: 'contact-centre-migration-reporting',
        title: 'Ensuring Data & Reporting Readiness For Contact Centre Platform Migration',
        clientType: 'General Retail (Customer Services)',
        duration: 'Ongoing Project (as Lead)',
        imageUrl: '/case-studies/contactcentre.webp', // UPDATED
        situation: 'A major organisation migrated its core contact centre platform (over 10 years old), requiring all critical data sources and Management Information (MI) reports to be identified, understood, and recreated/migrated before launch.',
        approach: 'Led the insight & reporting team for data migration. Coordinated identification/validation of required data feeds. Oversaw design, development, and testing of new MI reports compatible with the new platform for go-live readiness.',
        results: [
            'Successfully prepared all necessary data sources for platform migration.',
            'Ensured continuity of critical MI reporting from day one post-launch.',
            'Mitigated risks of data loss or reporting gaps during transition.',
            'Provided operations managers with necessary performance insights on the new platform.'
        ],
        relatedService: 'custom-reporting',
    },
    {
        id: 'automation-reporting-sql-vba',
        title: 'Automating Repetitive Reporting Tasks Using SQL & Power Automate At Major UK Retailer',
        clientType: 'Food Retail (Stockloss)',
        duration: '6 mos',
        imageUrl: '/case-studies/automation.webp', // UPDATED
        situation: 'Various roles involved significant time spent on manually compiling data from different sources (databases, spreadsheets) into regular reports, a process prone to errors and delays.',
        approach: 'Identified high-volume, repetitive reporting tasks suitable for automation. Developed scripts using Power Automate and SQL queries to automatically extract, transform, and consolidate data into predefined report templates. Scheduled tasks where appropriate.',
        results: [
        'Drastically reduced manual effort required for report generation (often by >90%).',
        'Improved report accuracy and consistency by eliminating manual data entry.',
        'Ensured timely delivery of critical business reports.',
        'Freed up analyst time for value-added analysis instead of data compilation.'
        ],
        relatedService: 'custom-reporting', // Also relates to custom reporting
    },
];